import type { QueryMeta } from '@tanstack/react-query';
import type { Schema } from 'yup';
import { ExceptionLogSchema } from '../data/exception-log';
import { OrderSchema } from '../data/order';
import { ProductSchema } from '../data/product';
import { ReceiptSchema } from '../data/receipt';
import { StoreSchema } from '../data/store';
import { UploadSchema } from '../data/upload';
import { VendorSchema } from '../data/vendor';

// The TanStack cache is persisted to IndexedDB and rehydrated as raw objects —
// rehydration never re-runs the data through these Yup schemas, so a field
// added to a schema (with a `.default()`) is missing on already-persisted rows
// until the background refetch replaces them. Consumers that read the new field
// then crash on stale-shape data (this is what broke the orders delivery
// indicator after the email-bounce deploy).
//
// To bound that, a persisted query tags itself with the row schema it's
// validated against, via `meta` (see persistMeta). The persister fingerprints
// that schema's SHAPE; when the shape changes (a field is added/removed/
// retyped), only that query's cache is evicted on the next load and refetched
// fresh — the rest of the persisted cache is untouched. The fingerprint is
// derived from the schema itself, so it tracks real schema changes with no
// version constant to bump.
//
// The mapped schema is the per-ROW schema (caches hold arrays validated
// row-by-row), so the fingerprint moves exactly when a row gains or loses a
// field. Add an entry here, and a `meta: persistMeta('<key>')` on the matching
// queryOptions, when a new persisted query renders straight from a response
// schema.
const PERSISTED_ROW_SCHEMAS = {
  stores: StoreSchema,
  orders: OrderSchema,
  products: ProductSchema,
  vendors: VendorSchema,
  receipts: ReceiptSchema,
  'exception-logs': ExceptionLogSchema,
  uploads: UploadSchema,
} satisfies Record<string, Schema>;

export type PersistResource = keyof typeof PERSISTED_ROW_SCHEMAS;

// fieldShape reduces a schema to its structural skeleton: nested object field
// names and array element types. It deliberately ignores validators,
// transforms, defaults, and required-ness — only a change that alters which
// fields exist (the rehydration hazard) should move the fingerprint.
function fieldShape(description: unknown): unknown {
  const node = description as {
    type?: string;
    fields?: Record<string, unknown>;
    innerType?: unknown;
  };

  if (node?.fields) {
    const fields: Record<string, unknown> = {};
    for (const key of Object.keys(node.fields).sort()) {
      fields[key] = fieldShape(node.fields[key]);
    }

    return { type: 'object', fields };
  }

  if (node?.innerType) {
    return { type: 'array', of: fieldShape(node.innerType) };
  }

  return { type: node?.type ?? 'unknown' };
}

const fingerprintCache = new Map<PersistResource, string>();

// fingerprintFor returns a stable string that changes iff the resource's row
// schema gains, loses, or retypes a field. Computed once per resource.
export function fingerprintFor(resource: PersistResource): string {
  const cached = fingerprintCache.get(resource);
  if (cached !== undefined) {
    return cached;
  }

  const fingerprint = JSON.stringify(fieldShape(PERSISTED_ROW_SCHEMAS[resource].describe()));
  fingerprintCache.set(resource, fingerprint);

  return fingerprint;
}

// persistMeta tags a persisted query with its row schema and that schema's
// current fingerprint, to be read back by the persister on restore. `resource`
// is constrained to the registered schemas, so a typo or an unregistered key is
// a compile error.
export function persistMeta(resource: PersistResource): QueryMeta {
  return { persistResource: resource, persistFingerprint: fingerprintFor(resource) };
}

// persistedQueryIsStale reports whether a restored query's tagged schema
// fingerprint no longer matches the current one — i.e. its row schema changed
// since it was persisted, so its rehydrated rows may be the wrong shape.
// Untagged or unregistered queries are never stale (left in the cache as-is).
export function persistedQueryIsStale(meta: QueryMeta | undefined): boolean {
  const resource = meta?.persistResource;
  if (typeof resource !== 'string' || !(resource in PERSISTED_ROW_SCHEMAS)) {
    return false;
  }

  return meta?.persistFingerprint !== fingerprintFor(resource as PersistResource);
}
